/**
 * @module app.cm
 */

import { BindingEngine, Disposable, ICollectionObserverSplice, autoinject, singleton } from 'aurelia-framework';
import { ComponentBase, IForm, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { CarePlanService } from 'app/cm/services/care-plan-service';
import { apiCall } from 'app/shared/lib/essette-api';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { localSort } from 'app/shared/lib/sorting';
import { I18N } from 'aurelia-i18n';
import { CasePageContext } from 'app/cm/lib/case-page-context';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { bindable } from 'aurelia-typed-observable-plugin';
import { IApiActionResults, IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { guid } from 'app/shared/lib/guid';
import * as CM from 'app/cm/models/essette-api';
import { pushCopy, spliceCopy } from 'app/shared/lib/utilities';


@autoinject
export class AddOutcomes extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull
    public caseId: string | null = null
    @bindable.boolean
    public showActions: boolean = false;
    @bindable.object({ changeHandler: 'loadCurrentOutcomes' })
    public goal: CM.CarePlanGoal | null = null;

    public outcomeSearchQuery: string | null = null;          // the value of the autocomplete textbox
    protected localSorting = new Sorting();

    protected outcomeSearchResults: CM.CarePlanOutcomeDefinition[] = [];     // array of search results from API for autocomplete (not currently used)
    protected currentOutcomes: CM.CarePlanOutcome[] = [];
    protected selectedOutcomes: CM.CarePlanOutcomeDefinition[] = [];
    protected insertOutcomes: CM.CarePlanOutcomeDefinition[] = [];
    protected selectedOutcomesSubscription: Disposable | null = null;
    protected outcomeLookups: CM.CarePlanOutcomeDefinition[] = [];
    protected outcomeOption: string = 'selectOutcomes';
    protected allowDuplicateOutcomes: boolean = false;

    protected cachedSortChangeEvent: SortChangeEvent | null = null;

    protected id: guid = guid();

    ////****************** Element References ***************************************////

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly carePlanService: CarePlanService,
        private readonly i18n: I18N,
        private readonly bindingEngine: BindingEngine,
        @singleton(true) private readonly context: CasePageContext,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {
        this.allowDuplicateOutcomes = !!getApiSetting(this.context.settings, 'carePlan.goals.outcomes.$allowDuplicates');
        this.loadOutcomeDefinitions();
        this.loadCurrentOutcomes();
    }

    protected attached(): void {
        this.observeSelectedOutcomes();
    }

    protected detached(): void {
        if(this.selectedOutcomesSubscription) this.selectedOutcomesSubscription.dispose();
    }

    ////****************** Events ***************************************************////
    protected selectedOutcomesChanged(splices: ICollectionObserverSplice<CM.CarePlanOutcomeDefinition>[]): void {
        if(this.insertOutcomes.length === 0 && this.selectedOutcomes.length === 0) return;

        for(var i = 0; i < splices.length; i++) {
            var splice = splices[i];

            var outcomesAdded = this.selectedOutcomes.slice(splice.index, splice.index + splice.addedCount);
            if(outcomesAdded.length > 0) {
                outcomesAdded.forEach(outcomeDefinition => {
                    this.insertOutcomes = pushCopy(this.insertOutcomes, outcomeDefinition);
                });
                // if the table is currently sorted, apply that sort to the newly added items
                if(this.cachedSortChangeEvent) this.applySort(this.cachedSortChangeEvent, this.insertOutcomes);
            }

            if(splice.removed.length) {
                (splice.removed as CM.CarePlanOutcomeDefinition[]).forEach(outcome => {
                    let i = this.insertOutcomes.find(o => o.id === outcome.id);
                    if(i) this.insertOutcomes = spliceCopy(this.insertOutcomes, this.insertOutcomes.indexOf(i), 1);
                });
            }
        }
    }

    ////****************** Functions ************************************************////
    protected addOutcome(outcome: CM.CarePlanOutcomeDefinition) {
        this.selectedOutcomes.push(outcome);
        this.outcomeSearchQuery = '';
        this.outcomeSearchResults.splice(0);
    }

    protected removeSelectedOutcome(outcome: CM.CarePlanOutcomeDefinition) {
        this.selectedOutcomes.splice(this.selectedOutcomes.indexOf(outcome, 0), 1);
    }

    protected observeSelectedOutcomes(): void {
        // observer only works on a modified array. if the array is replaced, the observer will not function for it any longer unless subscribed again
        if(this.selectedOutcomesSubscription) this.selectedOutcomesSubscription.dispose();

        this.selectedOutcomesSubscription = this.bindingEngine
            .collectionObserver(this.selectedOutcomes) // observe the selectedOutcomes array, which changes in the multi-select Outcome Definition Select element
            .subscribe(this.selectedOutcomesChanged.bind(this)); // pass the splice information (addedCount, index, removed) to selectedOutcomesChanged method

        // subscription disposed of in detached()
    }

    protected filterOutcomes = (outcome: CM.CarePlanOutcomeDefinition): boolean => {
        if(this.selectedOutcomes.some(o => outcome.id === o.id) && this.outcomeOption === 'searchOutcomes') {
            return false;
        }
        if(this.allowDuplicateOutcomes) { return true; }
        if(!this.currentOutcomes && !outcome) { return false; }
        return !this.currentOutcomes.some(o => outcome.id === (o.definition && o.definition.id));
    }

    protected isDuplicate(outcome: CM.CarePlanOutcomeDefinition) {
        return this.currentOutcomes.some(o => outcome.id === (o.definition && o.definition.id));
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent, outcomes: CM.CarePlanOutcome[]): void {
        const { property, direction } = event.detail;
        const { localSorting } = this;
        this.cachedSortChangeEvent = event;
        if(direction === null) {
            localSorting.remove(property);
            this.restoreDefaultOrder();
        } else {
            localSorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        localSort(outcomes, localSorting);
    }

    /** Restores table's display data to the order of original insertion */
    private restoreDefaultOrder(): void {
        this.insertOutcomes = [...this.selectedOutcomes];
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }
    public submittedChanges?: any;

    public async submit(): Promise<boolean> {
        return await this.saveSelectedOutcomes();
    }

    public reset(): void {
        this.selectedOutcomes.splice(0);
        this.outcomeSearchQuery = '';
        this.outcomeSearchResults.splice(0);
        this.outcomeOption = 'selectOutcomes';
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async searchOutcomes(newValue: string): Promise<void> {
        if(!newValue) {
            if(this.outcomeSearchResults) this.outcomeSearchResults.splice(0);
            return;
        }

        const fields = ['description'];

        try {
            this.state.beginLoad();

            const response = await this.carePlanService.searchOutcomeDefinitions(newValue, undefined, undefined, fields);
            this.outcomeSearchResults = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadCurrentOutcomes(): Promise<void> {
        if(!this.goal?.id) return;

        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const includes = ['definition'];
        const fields = ['definition.id', 'description'];

        try {
            this.state.beginLoad();
            const response = await this.carePlanService.loadOutcomesByGoal(this.goal.id, options, includes, fields);
            this.currentOutcomes = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadOutcomeDefinitions(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const fields = ['description'];

        try {
            this.state.beginLoad();
            const response = await this.carePlanService.loadOutcomeDefinitions(options, undefined, fields);
            this.outcomeLookups = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async saveSelectedOutcomes(): Promise<boolean> {
        const promises = new Array<Promise<IApiActionResults>>();

        if(!this.goal || !this.goal.id) {
            return Promise.resolve(false);
        }
        const duplicateOutcomes: string[] = [];

        const outcomesToSave = this.insertOutcomes
            .map(outcome => {
                if(this.allowDuplicateOutcomes && this.currentOutcomes.some(o => (o.definition && o.definition.id) === outcome.id)) {
                    duplicateOutcomes.push(outcome.description || '');
                }
                return {
                    goal: { id: this.goal!.id },
                    definition: { id: outcome.id }
                } as CM.CarePlanOutcome;
            });

        if(duplicateOutcomes.length > 0) {
            const message: string = this.i18n.tr('ui:messages.duplicate.confirm', { count: duplicateOutcomes.length, what: '$t(cm:carePlanOutcome.$)' });
            const result = await confirmation.showCustom(message, duplicateOutcomes.join('\n'), confirmation.options.StandardConfirm);
            if(result.dialogResponse === confirmation.options.CANCEL) {
                //canceling means we just stop
                return Promise.resolve(false);
            }
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();

            // Create a promise for each outcome
            outcomesToSave.forEach(outcome => {
                promises.push(this.carePlanService.insertCarePlanOutcome(outcome));
            });
            // Execute all promises and wait until they all finish
            const responses = await Promise.all(promises);
            isSuccessful = responses.every(r => r.isSuccessful);
            this.selectedOutcomes.splice(0);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
            return isSuccessful;
        }

    }

}

